import React, { useState, useRef } from 'react';
import { applyPhoneMask } from '../utils/masks';
import { motion, AnimatePresence } from 'framer-motion';
import type { User } from '../contexts/AuthContext';
import { User as UserIcon, Phone, Camera, X, Mail, Lock, Eye, EyeOff, AlertTriangle } from 'lucide-react';
import { ImageCropperModal } from './ImageCropperModal';

interface ProfileSettingsModalProps {
  currentUser: User;
  onClose: () => void;
  onUpdate: (updates: Partial<User>) => Promise<boolean>;
}

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({ currentUser, onClose, onUpdate }) => {
  const [firstName, setFirstName] = useState(currentUser.name ? currentUser.name.split(' ')[0] : '');
  const [lastName, setLastName] = useState(currentUser.name ? currentUser.name.split(' ').slice(1).join(' ') : '');
  const [phone, setPhone] = useState(currentUser.phone || '');
  const email = currentUser.email || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [photoUrl, setPhotoUrl] = useState(currentUser.photoUrl || '');
  const [cropperImageSrc, setCropperImageSrc] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Problem Reporting
  const [problemDescription, setProblemDescription] = useState('');

  const handleSendProblemReport = (e: React.MouseEvent) => {
    e.preventDefault();
    if (!problemDescription.trim()) {
      alert('Por favor, descreva o problema antes de enviar.');
      return;
    }

    const emailTo = 'paulo.souto@techtennisbr.com';
    const subject = encodeURIComponent('[TechTennis Web] Reporte de Problema');
    const body = encodeURIComponent(
      `Olá Paulo,\n\nUm usuário reportou um problema no sistema via aplicativo web.\n\n` +
      `Detalhes do Reporte:\n` +
      `- Descrição: ${problemDescription}\n\n` +
      `Informações do Usuário:\n` +
      `- Nome: ${currentUser.name}\n` +
      `- E-mail: ${currentUser.email}\n` +
      `- Perfil (Role): ${currentUser.role || 'N/D'}\n\n` +
      `Enviado via TechTennis Web App`
    );

    window.location.href = `mailto:${emailTo}?subject=${subject}&body=${body}`;
    setProblemDescription('');
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setCropperImageSrc(reader.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password && password !== confirmPassword) {
      alert('As senhas não coincidem!');
      return;
    }
    setLoading(true);
    const success = await onUpdate({ name: `${firstName} ${lastName}`.trim(), phone, photoUrl, email, password });
    setLoading(false);
    if (success) onClose();
    else alert('Erro ao atualizar perfil.');
  };

  return (
    <AnimatePresence>
      <div style={{ position: 'fixed', inset: 0, zIndex: 9999, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '24px' }}>
        <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} 
          style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(4px)' }} onClick={onClose} />
        
        <motion.div initial={{ scale: 0.95, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.95, opacity: 0 }} 
          className="glass-panel" style={{ width: '100%', maxWidth: '450px', padding: '32px', position: 'relative', zIndex: 10000, display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
          
          <button onClick={onClose} style={{ position: 'absolute', top: '16px', right: '16px', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
            <X size={24} />
          </button>

          <h2 style={{ fontSize: '24px', marginBottom: '24px', width: '100%', textAlign: 'center' }}>Meu Perfil</h2>
          
          <form onSubmit={handleSave} style={{ width: '100%', display: 'flex', flexDirection: 'column', gap: '20px' }}>
            
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '16px', marginBottom: '16px' }}>
              <div style={{ 
                width: '100px', height: '100px', borderRadius: '50%', background: photoUrl ? "url(" + photoUrl + ") center/cover" : '#2563EB', 
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'white', position: 'relative', overflow: 'hidden',
                border: '4px solid rgba(255,255,255,0.1)'
              }}>
                {!photoUrl && <UserIcon size={40} />}
              </div>
              <button type="button" onClick={() => fileInputRef.current?.click()} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'rgba(255,255,255,0.1)', border: 'none', padding: '8px 16px', borderRadius: '100px', color: 'white', cursor: 'pointer', fontSize: '13px' }}>
                <Camera size={16} /> Alterar Foto
              </button>
              <input type="file" ref={fileInputRef} accept="image/*" style={{ display: 'none' }} onChange={handleFileChange} />
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px' }}>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>Nome *</label>
                <div style={{ position: 'relative' }}>
                  <UserIcon style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} size={20} />
                  <input type="text" value={firstName} onChange={e => setFirstName(e.target.value)} required style={{ width: '100%', padding: '14px 14px 14px 48px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' }} />
                </div>
              </div>
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>Sobrenome *</label>
                <div style={{ position: 'relative' }}>
                  <UserIcon style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} size={20} />
                  <input type="text" value={lastName} onChange={e => setLastName(e.target.value)} required style={{ width: '100%', padding: '14px 14px 14px 48px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' }} />
                </div>
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>Celular</label>
              <div style={{ position: 'relative' }}>
                <Phone style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} size={20} />
                <input type="text" value={phone} onChange={e => setPhone(applyPhoneMask(e.target.value))} style={{ width: '100%', padding: '14px 14px 14px 48px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>E-mail</label>
              <div style={{ position: 'relative' }}>
                <Mail style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'rgba(255,255,255,0.3)' }} size={20} />
                <input type="email" value={email} disabled required style={{ width: '100%', padding: '14px 14px 14px 48px', borderRadius: '12px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.05)', color: 'rgba(255,255,255,0.4)', cursor: 'not-allowed', outline: 'none' }} />
              </div>
            </div>

            <div>
              <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>Nova Senha (opcional)</label>
              <div style={{ position: 'relative' }}>
                <Lock style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} size={20} />
                <input type={showPassword ? "text" : "password"} autoComplete="new-password" placeholder="Deixe em branco para manter a atual" value={password} onChange={e => setPassword(e.target.value)} style={{ width: '100%', padding: '14px 48px 14px 48px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' }} />
                <button type="button" onClick={() => setShowPassword(!showPassword)} style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>
            </div>
            
            {password && (
              <div>
                <label style={{ display: 'block', marginBottom: '8px', color: 'var(--text-secondary)', fontSize: '14px' }}>Confirmar Nova Senha</label>
                <div style={{ position: 'relative' }}>
                  <Lock style={{ position: 'absolute', top: '50%', left: '16px', transform: 'translateY(-50%)', color: 'var(--text-secondary)' }} size={20} />
                  <input type={showConfirmPassword ? "text" : "password"} autoComplete="new-password" placeholder="Confirme a nova senha" value={confirmPassword} onChange={e => setConfirmPassword(e.target.value)} style={{ width: '100%', padding: '14px 48px 14px 48px', borderRadius: '12px', background: 'rgba(0,0,0,0.2)', border: '1px solid rgba(255,255,255,0.1)', color: 'white', fontSize: '15px' }} />
                  <button type="button" onClick={() => setShowConfirmPassword(!showConfirmPassword)} style={{ position: 'absolute', top: '50%', right: '16px', transform: 'translateY(-50%)', background: 'none', border: 'none', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                    {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>
            )}

            <div style={{ marginTop: '16px', display: 'flex', flexDirection: 'column', gap: '12px', width: '100%' }}>
              <button type="submit" disabled={loading} className="button-primary" style={{ width: '100%', padding: '16px' }}>
                {loading ? 'Salvando...' : 'Salvar Perfil'}
              </button>
            </div>

            <div style={{ width: '100%', marginTop: '24px', paddingTop: '24px', borderTop: '1px solid rgba(255,255,255,0.1)', display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <h4 style={{ fontSize: '14px', color: 'var(--text-secondary)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0, alignSelf: 'flex-start' }}>
                Reportar um Problema
              </h4>
              <textarea
                placeholder="Descreva o problema que você encontrou..."
                value={problemDescription}
                onChange={e => setProblemDescription(e.target.value)}
                style={{
                  width: '100%',
                  minHeight: '80px',
                  padding: '12px',
                  background: 'rgba(0, 0, 0, 0.2)',
                  border: '1px solid rgba(255, 255, 255, 0.1)',
                  borderRadius: '12px',
                  color: 'white',
                  fontSize: '14px',
                  outline: 'none',
                  resize: 'vertical',
                }}
              />
              <button
                type="button"
                onClick={handleSendProblemReport}
                style={{
                  width: '100%',
                  padding: '12px',
                  background: 'rgba(239, 68, 68, 0.1)',
                  border: '1px solid rgba(239, 68, 68, 0.3)',
                  borderRadius: '12px',
                  color: '#f87171',
                  fontSize: '14px',
                  fontWeight: 600,
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px',
                  transition: 'all 0.2s',
                }}
                onMouseOver={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.2)'}
                onMouseOut={e => e.currentTarget.style.background = 'rgba(239, 68, 68, 0.1)'}
              >
                <AlertTriangle size={16} /> Enviar Relatório por E-mail
              </button>
            </div>
          </form>
        </motion.div>
      </div>

      {cropperImageSrc && (
        <ImageCropperModal
          isOpen={!!cropperImageSrc}
          imageSrc={cropperImageSrc}
          onClose={() => setCropperImageSrc(null)}
          onCropComplete={(croppedBase64) => {
            setPhotoUrl(croppedBase64);
            setCropperImageSrc(null);
          }}
        />
      )}
    </AnimatePresence>
  );
};
